'use client';

import { useEffect, useState } from 'react';
import { ddRumError } from '@/utils/datadog';
import { DD_USE_CASES } from '@/utils/datadog/types';

let initialized: boolean = false;
let initializationPromise: Promise<void> | null = null;

const initializeStreamingUM = async (): Promise<void> => {
    if (initialized) {
        return;
    }

    if (initializationPromise) {
        await initializationPromise;
        return;
    }

    initializationPromise = (async () => {
        const { initializeUnifiedModules } = await import('@/logic/unified-modules');
        await initializeUnifiedModules();

        const { CasinoStreamingUm } = await import('@fanduel/casino-streaming-um-web');

        await CasinoStreamingUm.initialize();

        initialized = true;
    })();

    try {
        await initializationPromise;
    } catch (error) {
        initializationPromise = null;
        throw error;
    }
};

export const useStreamingUm = (enabled: boolean) => {
    const [ready, setReady] = useState<boolean>(initialized);

    useEffect(() => {
        if (!enabled) {
            return;
        }

        let active = true;

        void initializeStreamingUM()
            .then(() => {
                if (active) {
                    setReady(true);
                }
            })
            .catch((error) => {
                ddRumError({
                    name: 'STREAMING_UM_INIT_FAILED',
                    description: 'Failed to initialize the casino streaming UM',
                    use_case: DD_USE_CASES.STREAMING,
                    context: { message: error instanceof Error ? error.message : String(error) }
                });
            });

        return () => {
            active = false;
        };
    }, [enabled]);

    return { ready };
};
